/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	forwardRef,
	type ForwardRefExoticComponent,
	type RefAttributes,
	useCallback,
	useImperativeHandle,
	useRef,
} from 'react';

import Lottie, { type LottieRefCurrentProps } from 'lottie-react';

import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	container: {
		width: '100%',
		height: '100%',
		borderRadius: token('radius.xlarge'),
		overflow: 'hidden',
	},
});

export type LottieRendererHandle = {
	play: () => void;
	pause: () => void;
};

export type LottieRendererProps = {
	/**
	 * Parsed Lottie JSON to render.
	 */
	lottieFile: object;
	/**
	 * Accessible label; mirrors the parent `MotionPlayer`'s `aria-label`.
	 */
	label: string;
	/**
	 * Id of an external element (e.g. a caption) to associate with the asset
	 * via `aria-describedby`.
	 */
	describedById?: string;
	/**
	 * Called when a loop completes; parent resets "playing" and rewinds.
	 */
	onLoopComplete: () => void;
};

/**
 * __LottieRenderer__
 *
 * Lottie-specific renderer for `MotionPlayer`. Exposes `play`/`pause` via
 * an imperative ref so the parent's `PlayPauseControl` drives playback
 * without re-rendering.
 */
const LottieRenderer: ForwardRefExoticComponent<
	LottieRendererProps & RefAttributes<LottieRendererHandle>
> = forwardRef<LottieRendererHandle, LottieRendererProps>(
	({ lottieFile, label, describedById, onLoopComplete }, ref) => {
		const lottieRef = useRef<LottieRefCurrentProps>(null);
		const containerRef = useRef<HTMLDivElement>(null);

		useImperativeHandle(
			ref,
			() => ({
				play: () => lottieRef.current?.play(),
				pause: () => lottieRef.current?.pause(),
			}),
			[],
		);

		/* Expand SVG filter bounds so Lottie exports that use filters don't
		 * get clipped at the edges. */
		const patchSvgFilterBounds = useCallback(() => {
			const container = containerRef.current;
			if (!container) {
				return;
			}
			container.querySelectorAll('filter').forEach((filter) => {
				filter.setAttribute('x', '-10%');
				filter.setAttribute('y', '-10%');
				filter.setAttribute('width', '120%');
				filter.setAttribute('height', '120%');
			});
		}, []);

		const handleLoopComplete = useCallback(() => {
			onLoopComplete();
			/* `true` = arg is a frame number, not milliseconds. Stays correct
			 * for animations exported with a non-zero in-point. */
			lottieRef.current?.goToAndStop(0, true);
		}, [onLoopComplete]);

		return (
			<div ref={containerRef} css={styles.container}>
				<Lottie
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Lottie's own wrapper only accepts an inline style prop.
					style={{ height: '100%', width: '100%' }}
					lottieRef={lottieRef}
					animationData={lottieFile}
					autoplay={false}
					loop={true}
					onDOMLoaded={patchSvgFilterBounds}
					onLoopComplete={handleLoopComplete}
					role="img"
					aria-label={label}
					aria-describedby={describedById}
				/>
			</div>
		);
	},
);

LottieRenderer.displayName = 'LottieRenderer';

export default LottieRenderer;
